import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Modal, Typography, Upload, Button, message } from 'antd'
import { Icon } from '@iconify/react'
import { importExcelTasksThunk } from '../../taskRefactored/tasks.slice'
import { showActionNotification } from '../../../components/notification'

const { Title, Text } = Typography

const ImportTasksModal = ({ open, onCancel, projectId, onImportSuccess }) => {
    const dispatch = useDispatch()
    const [fileList, setFileList] = useState([])
    const [loading, setLoading] = useState(false)

    // Clear file list when modal is closed
    useEffect(() => {
        if (!open) {
            setFileList([])
        }
    }, [open])

    const handleContinue = async () => {
        if (!fileList || fileList.length === 0) {
            message.warning('Please select a file to import')
            return
        }

        if (!projectId) {
            message.error('Project ID is required')
            return
        }

        setLoading(true)
        try {
            const file = fileList[0].originFileObj || fileList[0]
            const result = await dispatch(
                importExcelTasksThunk({
                    file,
                    projectId,
                })
            )

            const data = result?.payload?.data

            if (data) {
                const { success, message: responseMessage } = data

                if (success) {
                    showActionNotification({
                        message: 'Tasks imported successfully!',
                        subMessage: responseMessage?.message,
                    })
                    setFileList([])
                    if (onImportSuccess) {
                        onImportSuccess()
                    }
                    onCancel()
                } else {
                    setFileList([])
                    showActionNotification({
                        message: 'Failed to import tasks',
                        subMessage: responseMessage?.error,
                    })
                }
            }
        } catch (error) {
            showActionNotification({
                message: 'Failed to import tasks',
            })
        } finally {
            setFileList([])
            setLoading(false)
        }
    }

    const handleFileChange = (info) => {
        const { fileList: newFileList } = info
        setFileList(newFileList)
    }

    const handleRemove = () => {
        setFileList([])
    }

    return (
        <Modal
            title={null}
            open={open}
            onCancel={onCancel}
            footer={null}
            width={600}
            centered
            closeIcon={
                <Icon
                    icon="mdi:close"
                    style={{ fontSize: 20, color: '#64748b' }}
                />
            }
            styles={{
                content: {
                    borderRadius: '12px',
                    padding: '0',
                },
                body: {
                    padding: '32px',
                },
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                }}
            >
                {/* Modal Header */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <Icon
                        icon="mdi:sparkles"
                        width={20}
                        height={20}
                        style={{ color: '#6366f1' }}
                    />
                    <Title
                        level={4}
                        style={{
                            margin: 0,
                            fontSize: '20px',
                            fontWeight: 600,
                        }}
                    >
                        Import tasks
                    </Title>
                </div>

                {/* Instructions */}
                <Text
                    style={{
                        fontSize: '14px',
                        color: '#64748b',
                        lineHeight: '20px',
                    }}
                >
                    Upload an Excel (.xlsx, .xls) or CSV file to import multiple
                    tasks into this project at once.
                </Text>

                {/* File Upload Area */}
                <Upload.Dragger
                    style={{
                        border: '2px dashed #cbd5e1',
                        borderRadius: '8px',
                        backgroundColor: '#f8fafc',
                        padding: '40px 20px',
                    }}
                    fileList={fileList}
                    onChange={handleFileChange}
                    onRemove={handleRemove}
                    beforeUpload={() => false}
                    accept=".xlsx,.xls,.csv"
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '12px',
                        }}
                    >
                        <div
                            style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '8px',
                                backgroundColor: '#e0e7ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Icon
                                icon="mdi:file-upload-outline"
                                width={24}
                                height={24}
                                style={{ color: '#6366f1' }}
                            />
                        </div>
                        <Text
                            style={{
                                fontSize: '14px',
                                fontWeight: 500,
                                color: '#1e293b',
                            }}
                        >
                            Select or drop a file
                        </Text>
                    </div>
                </Upload.Dragger>

                {/* Continue Button */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: '8px',
                    }}
                >
                    <Button
                        onClick={handleContinue}
                        loading={loading}
                        disabled={!fileList || fileList.length === 0}
                        style={{
                            height: '40px',
                            padding: '0 16px',
                            borderRadius: '8px',
                            fontWeight: 500,
                            fontSize: '14px',
                            border: '1px solid #e2e8f0',
                            background: '#ffffff',
                            color: '#475569',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        Continue
                    </Button>
                </div>
            </div>
        </Modal>
    )
}

export default ImportTasksModal
